import { useId, useState } from "react";

import { Button } from "@components/ui/button";
import { Input } from "@components/ui/input";
import { Label } from "@components/ui/label";

// Mirrors the backend/cosmos-service bound on one assessed condition.
const MAX_CONDITION_LENGTH = 200;

type AssessedConditionFieldProps = {
  /** The condition currently applied to Quick Lookups; "" when none. */
  value: string;
  /** Apply a new condition ("" removes it). The caller re-runs the lookup. */
  onApply: (condition: string) => void;
  disabled?: boolean;
};

/**
 * Optional "condition being assessed" for a Quick Lookup, e.g. "Right shoulder
 * injury". In files covering several conditions it keeps answers to the
 * records about this one. The copilot doesn't store the condition on the case,
 * so the expert enters it here.
 */
export const AssessedConditionField = ({
  value,
  onApply,
  disabled = false,
}: AssessedConditionFieldProps) => {
  const inputId = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (value && !editing) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
        <span className="text-foreground-muted">Condition being assessed:</span>
        <span className="font-medium text-foreground">{value}</span>
        <Button
          type="button"
          variant="simple-outline"
          size="compact"
          disabled={disabled}
          onClick={() => {
            setDraft(value);
            setEditing(true);
          }}
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <form
      className="mt-3"
      onSubmit={(e) => {
        e.preventDefault();
        setEditing(false);
        const next = draft.replace(/\s+/g, " ").trim();
        if (next !== value) onApply(next);
      }}
    >
      <Label htmlFor={inputId} className="text-xs text-foreground-muted">
        Condition being assessed (optional)
      </Label>
      <div className="mt-1.5 flex items-center gap-2">
        <Input
          id={inputId}
          value={draft}
          maxLength={MAX_CONDITION_LENGTH}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="e.g. Right shoulder injury"
          className="h-8 min-h-8 flex-1 text-sm"
        />
        <Button
          type="submit"
          variant="secondary-subtle"
          size="compact"
          disabled={disabled}
        >
          {value && !draft.trim() ? "Remove" : "Apply"}
        </Button>
      </div>
    </form>
  );
};
